import { isString } from '@dimjs/lang';
import { classNames } from '@dimjs/utils';
import { flatbizDate, isUndefinedOrNull, noop } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button } from '../button';
import { IphoneBottomAdaptive } from '../iphone-bottom-adaptive';
import { ModalDrawer, ModalDrawerProps } from '../modal-drawer';
import { PickerColumnItem } from '../picker/types';
import { Tabs } from '../tabs';
import { DateView, DateViewProps } from './date-view';
import './styles/date-range-picker.less';

export type DateRangePickerChangeParam = {
  label: string;
  value: Array<PickerColumnItem<string> | null>;
};

export interface DateRangePickerProps {
  title?: string | JSX.Element;
  closeIcon?: boolean;
  visible?: boolean;
  value?: [string | PickerColumnItem<string> | null, string | PickerColumnItem<string> | null];
  onChange?: (value?: DateRangePickerChangeParam) => void;
  modalProps?: Omit<ModalDrawerProps, 'visible' | 'className' | 'title' | 'closeIcon'>;
  onClose?: () => void;
  // 开始时间、结束时间选择时，是否联动
  isUnite?: boolean;
  dateViewProps: { minDate: string; maxDate: string };
  className?: string;
  leftOperateBtn?: {
    btnName: string;
    onClick?: () => void;
  };
  header?: JSX.Element;
  startDateIsRequired?: boolean;
}

export type DateRangePickerRefApi = {
  onChangeTab: (tab: 'left' | 'right') => void;
};

/**
 * 选择日期区间
 * ```
 * 1. value可设置开始时间和结束时间，['2022-01-01', '2022-02-02']
 *
 * 2. 如果minDate、maxDate数据有异常，则系统为minDate、maxDate重新赋值，赋值规则为value设置的前后两年
 * ```
 */

export const DateRangePicker = forwardRef<DateRangePickerRefApi, DateRangePickerProps>((props, ref) => {
  const currentStartDateRef = useRef('');
  const currentEndDateRef = useRef('');
  const minDateRef = useRef('');
  const maxDateRef = useRef('');

  const [activeKey, setActiveIndex] = useState('1');
  // 操作切换的时间
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // 已选择显示时间
  const [initiativeStartDate, setInitiativeStartDate] = useState('');
  const [initiativeEndDate, setInitiativeEndDate] = useState('');

  const value = useMemo(() => {
    const v = props.value || [];
    return v.map((temp) => {
      temp = isUndefinedOrNull(temp)
        ? null
        : isString(temp)
        ? temp
        : (temp as PickerColumnItem<string>).value;
      return temp as string | null;
    }) as [string | null, string | null];
  }, [props.value]);

  hooks.useDeepCompareEffect(() => {
    let defaultStartDate = value?.[0];
    let defaultEndDate = value?.[1];
    let currentStartDate = defaultStartDate;
    let currentEndDate = defaultEndDate;
    let { minDate, maxDate } = props.dateViewProps;
    if (!currentStartDate || !flatbizDate.isDate(currentStartDate)) {
      defaultStartDate = null;
      currentStartDate = flatbizDate.format(new Date(), 'YYYY-MM-DD');
    }
    if (!currentEndDate || !flatbizDate.isDate(currentEndDate)) {
      defaultEndDate = null;
      currentEndDate = flatbizDate.format(new Date(), 'YYYY-MM-DD');
    }
    if (flatbizDate.gt(currentStartDate, currentEndDate)) {
      const temp = currentStartDate;
      currentStartDate = currentEndDate;
      currentEndDate = temp;
    }
    currentStartDate = flatbizDate.format(currentStartDate, 'YYYY-MM-DD');
    currentEndDate = flatbizDate.format(currentEndDate, 'YYYY-MM-DD');
    // 如果minDate、maxDate数据有异常，则系统为minDate、maxDate重新赋值，赋值规则为currentDate的前后两年
    if (!flatbizDate.isDate(minDate)) {
      minDate = flatbizDate.format(flatbizDate.update(currentStartDate, 'y', 2), 'YYYY-MM-DD');
    } else {
      minDate = flatbizDate.format(minDate, 'YYYY-MM-DD');
    }
    if (!flatbizDate.isDate(maxDate)) {
      maxDate = flatbizDate.format(flatbizDate.update(currentStartDate, 'y', -2), 'YYYY-MM-DD');
    } else {
      maxDate = flatbizDate.format(maxDate, 'YYYY-MM-DD');
    }
    if (flatbizDate.gt(currentEndDate, maxDate)) {
      console.warn(`当前指定时间【${currentEndDate}】大于最大设置时间【${maxDate}】`);
      currentEndDate = maxDate;
    }
    if (flatbizDate.gt(minDate, currentStartDate)) {
      console.warn(`当前指定时间【${currentStartDate}】小于最小设置时间【${minDate}】`);
      currentStartDate = minDate;
    }

    currentStartDateRef.current = currentStartDate;
    currentEndDateRef.current = currentEndDate;

    minDateRef.current = minDate;
    maxDateRef.current = maxDate;

    setStartDate(currentStartDateRef.current);
    setEndDate(currentEndDateRef.current);
    if (props.startDateIsRequired) {
      setInitiativeStartDate(currentStartDateRef.current);
    } else {
      if (defaultStartDate) {
        setInitiativeStartDate(flatbizDate.format(defaultStartDate, 'YYYY-MM-DD'));
      } else {
        setInitiativeStartDate('');
      }
    }
    if (defaultEndDate) {
      setInitiativeEndDate(flatbizDate.format(defaultEndDate, 'YYYY-MM-DD'));
    } else {
      setInitiativeEndDate('');
    }
  }, [props.value, props.dateViewProps]);

  useImperativeHandle(ref, () => {
    return {
      onChangeTab: (param) => {
        if (param === 'left') {
          onTabChange('1');
        } else {
          onTabChange('2');
        }
      },
    };
  });

  const onTabChange = hooks.useCallbackRef((key: string) => {
    setActiveIndex(key);
    if (key === '2' && !initiativeEndDate) {
      setInitiativeEndDate(endDate);
    } else if (key === '1' && !initiativeStartDate) {
      setInitiativeStartDate(startDate);
    }
  });

  const onStartDateChange = hooks.useCallbackRef((startDateItem: PickerColumnItem) => {
    const startDate = startDateItem.value as string;
    setStartDate(startDate);
    setInitiativeStartDate(startDate);
    if (props.isUnite) {
      if (flatbizDate.gt(startDate, endDate)) {
        setEndDate(startDate);
        setInitiativeEndDate(startDate);
      }
    }
  });
  const onEndDateChange = hooks.useCallbackRef((endDateItem: PickerColumnItem) => {
    const endDate = endDateItem.value as string;
    setEndDate(endDate);
    setInitiativeEndDate(endDate);
  });

  const onLeftOperateBtn = hooks.useCallbackRef(() => {
    if (props.leftOperateBtn?.onClick) {
      props.leftOperateBtn.onClick();
      return;
    }
    setStartDate(currentStartDateRef.current);
    setEndDate(currentEndDateRef.current);
    setInitiativeStartDate('');
    setInitiativeEndDate('');
  });

  const onConfirm = hooks.useCallbackRef(() => {
    let selectedStartDate = initiativeStartDate;
    let selectedEndDate = initiativeEndDate;
    if (initiativeStartDate && initiativeEndDate) {
      if (flatbizDate.gt(initiativeStartDate, initiativeEndDate)) {
        setStartDate(initiativeEndDate);
        setEndDate(initiativeStartDate);

        setInitiativeStartDate(initiativeEndDate);
        setInitiativeEndDate(initiativeStartDate);

        selectedStartDate = initiativeEndDate;
        selectedEndDate = initiativeStartDate;
      }
    }
    if (selectedStartDate && selectedEndDate) {
      props.onChange?.({
        label: `${selectedStartDate}~${selectedEndDate}`,
        value: [
          { label: selectedStartDate, value: selectedStartDate },
          { label: selectedEndDate, value: selectedEndDate },
        ],
      });
    } else if (selectedStartDate) {
      props.onChange?.({
        label: selectedStartDate,
        value: [{ label: selectedStartDate, value: selectedStartDate }],
      });
    } else if (selectedEndDate) {
      props.onChange?.({
        label: selectedEndDate,
        value: [null, { label: selectedEndDate, value: selectedEndDate }],
      });
    } else {
      props.onChange?.(undefined);
    }
  });

  const startDateProps: DateViewProps = {
    minDate: minDateRef.current,
    maxDate: maxDateRef.current,
    onChange: onStartDateChange,
    defaultDate: currentStartDateRef.current,
    value: startDate,
  };

  const endDateProps: DateViewProps = {
    minDate: props.isUnite ? startDate : minDateRef.current,
    maxDate: maxDateRef.current,
    onChange: onEndDateChange,
    defaultDate: currentEndDateRef.current,
    value: endDate,
  };

  if (
    !currentStartDateRef.current ||
    !currentEndDateRef.current ||
    !minDateRef.current ||
    !maxDateRef.current
  ) {
    return null;
  }

  return (
    <ModalDrawer
      closeIcon={props.closeIcon}
      onClose={props.onClose}
      {...props.modalProps}
      visible={props.visible || false}
      className={classNames('date-range-picker', props.className)}
      title={props.title}
    >
      {props.header}
      <div className="date-range-content">
        <div className={classNames('show-range-date', `show-range-${activeKey}`)}>
          <div onClick={onTabChange.bind(null, '1')}>{initiativeStartDate || '--'}</div>
          <div>至</div>
          <div onClick={onTabChange.bind(null, '2')}>{initiativeEndDate || '--'}</div>
        </div>
        <div className="date-picker-tabs">
          <Tabs onChange={noop} activeKey={activeKey} height={500} hideTabsHeader={true} tabsType="fixed">
            <Tabs.Pane title="" key="1">
              <DateView {...startDateProps} />
            </Tabs.Pane>
            <Tabs.Pane title="" key="2">
              <DateView {...endDateProps} />
            </Tabs.Pane>
          </Tabs>
        </div>
        <div className="date-range-operate">
          <Button onClick={onLeftOperateBtn} fill="outline">
            {props.leftOperateBtn?.btnName || '清空'}
          </Button>
          <Button type="primary" onClick={onConfirm}>
            确定
          </Button>
        </div>
        <IphoneBottomAdaptive />
      </div>
    </ModalDrawer>
  );
});

DateRangePicker.defaultProps = {
  closeIcon: true,
};

DateRangePicker['domTypeName'] = 'DateRangePicker';
